import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop } from 'underscore';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidatedVideoExperience, getODataConfig } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import {
  ImageListSummaryCardDetails,
} from '@bingads-webui-campaign-react/pmax-image-experience';

import { PANEL_KEYS } from '@bingads-webui-campaign-react/wizard-shared-components';

const DisplayPlusMaxVideoCount = 5;

export const VideoPanel = ({
  perfMarker,
  mergedI18n,
  videos,
  setSelectedVideoId = noop,
  selectedVideoId,
  icon,
  allAssetsExpand,
  isVideoRequired,
  topicKey,
  skipQualityValidation,
  loadClipchampPlayer,
  ...rest
}) => {
  const permissions = useContext(PermissionsContext);

  const serverContext = useContext(ServerContext);

  const i18n = useContext(I18nContext);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const { scope } = useContext(ScopeContext);

  const { cid, aid } = scope.values;

  const videoTypesWithOrder = useMemo(() => (['VideoMedia16X9']), []);

  const videosContainer = useMemo(() => {
    const videoAd = { videos: videos || [] };

    return (
      <ValidatedVideoExperience
        i18n={mergedI18n}
        perfMarker={perfMarker}
        permissions={permissions}
        oDataConfig={oDataConfig}
        cid={cid}
        aid={aid}
        multiVideoMode
        allowedVideoSizes={videoTypesWithOrder}
        disableRemoveAll
        hideRatioLabel
        videoAd={videoAd}
        videoExperienceCircleUploadType
        disableUploadTextField
        videoPickerContainerHeight="auto"
        setSelectedVideoIdx={setSelectedVideoId}
        selectedVideoIdx={selectedVideoId}
        copilotEnabled
        isVideoRequired={isVideoRequired}
        skipQualityValidation={skipQualityValidation}
        loadClipchampPlayer={loadClipchampPlayer}
      />
    );
  }, [videos, mergedI18n, perfMarker, permissions, videoTypesWithOrder, selectedVideoId, oDataConfig, cid, aid, setSelectedVideoId, isVideoRequired, skipQualityValidation, loadClipchampPlayer]);

  return (
    <ValidatedPanel
      key={PANEL_KEYS.VIDEOS}
      title={<TextTitle
        titleKey={_TL_('Videos')}
        topicKey={topicKey || 'pop_BA_VideoAds_VideoSource'}
        icon={icon}
        iconAltText={i18n.getString(_TL_('"Videos" panel\'s image'))}
        details={<ImageListSummaryCardDetails i18n={i18n} videos={videos} isLogos={false} isVideos />}
      />}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      actuationProps={{
      expand: allAssetsExpand,
      expandKey: PANEL_KEYS.VIDEOS,
      }}
      {...rest}
    >
      <span>{i18n.getString(_TL_('You can add up to {{limit}} videos to your ad.'), { limit: DisplayPlusMaxVideoCount })}</span>
      <div className={classNames('imageExperienceStyle')}>
        {videosContainer}
      </div>
    </ValidatedPanel>
  );
};

VideoPanel.propTypes = {
  perfMarker: PropTypes.string.isRequired,
  mergedI18n: PropTypes.shape({}).isRequired,
  videos: PropTypes.shape([]).isRequired,
  setSelectedVideoId: PropTypes.func,
  selectedVideoId: PropTypes.number.isRequired,
  skipQualityValidation: PropTypes.bool,
  icon: PropTypes.node.isRequired,
  allAssetsExpand: PropTypes.func.isRequired,
  isVideoRequired: PropTypes.bool,
  topicKey: PropTypes.string,
  loadClipchampPlayer: PropTypes.func,
};

VideoPanel.defaultProps = {
  isVideoRequired: false,
  topicKey: null,
  skipQualityValidation: false,
  loadClipchampPlayer: () => {},
};
